import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import firebase from "gatsby-plugin-firebase";
import { FEATURED_JOB_SET, FETCH_JOBS_LIMIT } from "../../utl/constants";

// Initial state
export const initialState = {
  allJobs: [],
  allJobsFetched: [],
  latestJobsFetched: [],
  isFetching: false,
  myJobsIsLoading: false,
  myJobsHasError: false,
  myJobsHasFulfilled: false,
  featuredJobsIsLoading: false,
  featuredJobsHasError: false,
  featuredJobsHasFulfilled: false,
};
// Store the last fetched Firestore document for pagination
// Firestore docs can't be serialized, so not stored in Redux store
const queryStart = {};

// Thunks
export const getJobs = createAsyncThunk(
  "jobs/getJobs",
  async (missionAreaId) => {
    try {
      let querySnapshot;
      if (queryStart[`${missionAreaId}`] === undefined) {
        // We haven't fetched any jobs for this mission area yet
        querySnapshot = await firebase
          .firestore()
          .collection("job-posts")
          .where("missionAreas", "array-contains", missionAreaId)
          .where("active", "==", true)
          .limit(FETCH_JOBS_LIMIT)
          .orderBy("dateAdded", "desc")
          .get();
      } else {
        // Fetch jobs, starting from the last doc
        querySnapshot = await firebase
          .firestore()
          .collection("job-posts")
          .where("missionAreas", "array-contains", missionAreaId)
          .where("active", "==", true)
          .limit(FETCH_JOBS_LIMIT)
          .orderBy("dateAdded", "desc")
          .startAfter(queryStart[`${missionAreaId}`])
          .get();
      }

      if (querySnapshot.empty) {
        // There are no jobs (or no additional jobs) in this mission area
        return { jobs: [], missionAreaId, endOfPosts: true };
      } else {
        // Store the last document for pagination
        queryStart[`${missionAreaId}`] = querySnapshot.docs.slice(-1).pop();

        // Return an array of recent jobs
        const jobs = querySnapshot.docs.map((doc) => {
          const data = { ...doc.data(), id: doc.id };

          // Date object is not serializable, so convert to milliseconds
          if (data.dateAdded !== undefined) {
            data.dateAdded = data.dateAdded.toMillis();
          }
          if (data.expires !== undefined) {
            data.expires = data.expires.toMillis();
          }

          return data;
        });

        // If the limit wasn't reached, we're at the end of the list
        if (querySnapshot.docs.length < FETCH_JOBS_LIMIT) {
          return { jobs, missionAreaId, endOfPosts: true };
        } else {
          return { jobs, missionAreaId, endOfPosts: false };
        }
      }
    } catch (error) {
      // Log an event
      firebase.analytics().logEvent("get_recent_jobs", {
        errorCode: error.code,
        errorMsg: error.message,
      });

      // Return as if there are no jobs
      return { jobs: [], missionAreaId, endOfPosts: true };
    }
  }
);

export const getJob = createAsyncThunk("jobs/getJob", async (jobId) => {
  try {
    const jobSnapshot = await firebase
      .firestore()
      .collection("job-posts")
      .doc(jobId)
      .get();
    if (!jobSnapshot.exists) {
      // This job doesn't exist, so return null
      return { id: jobId, jobTitle: null };
    } else {
      // Return the job data
      const jobData = jobSnapshot.data();

      // Date object is not serializable, so convert to milliseconds
      if (jobData.dateAdded !== undefined) {
        jobData.dateAdded = jobData.dateAdded.toMillis();
      }
      if (jobData.expires !== undefined) {
        jobData.expires = jobData.expires.toMillis();
      }

      return { ...jobData, id: jobId };
    }
  } catch (error) {
    // Log an event
    firebase.analytics().logEvent("get_job", {
      errorCode: error.code,
      errorMsg: error.message,
    });

    // Return as if the job doesn't exist
    return { id: jobId, jobTitle: null };
  }
});

export const getMyJobs = createAsyncThunk("jobs/getMyJobs", async () => {
  try {
    const querySnapshot = await firebase
      .firestore()
      .collection("job-posts")
      .where(`roles.${firebase.auth().currentUser.uid}`, ">", "")
      .get();
    if (querySnapshot.empty) {
      // This user isn't admin/publisher of any jobs
      return undefined;
    } else {
      let myJobs = querySnapshot.docs;

      // Sort by dateAdded (not supported by Firestore with get)
      if (myJobs.length >= 2) {
        // Only sort if necessary
        myJobs.sort((a, b) => {
          const dataA = a.data();
          const dataB = b.data();

          // Converts to Date from Firestore timestamp first using toDate()
          return (
            dataB.dateAdded.toDate().getTime() -
            dataA.dateAdded.toDate().getTime()
          );
        });
      }

      // Return the job data
      return myJobs.map((doc) => {
        const jobData = doc.data();
        // Convert non-serializable values
        if (jobData.dateAdded !== undefined) {
          jobData.dateAdded = jobData.dateAdded.toMillis();
        }
        if (jobData.expires !== undefined) {
          jobData.expires = jobData.expires.toMillis();
        }
        return { ...jobData, id: doc.id };
      });
    }
  } catch (error) {
    // Log an event
    firebase.analytics().logEvent("get_my_jobs", {
      errorCode: error.code,
      errorMsg: error.message,
    });

    // Return as if the user isn't admin/publisher of any jobs
    return undefined;
  }
});

export const getFeaturedJobs = createAsyncThunk(
  "jobs/getFeaturedJobs",
  async (featuredJobSet) => {
    try {
      const querySnapshot = await firebase
        .firestore()
        .collection("job-posts")
        .where(
          "featured",
          "==",
          featuredJobSet !== undefined ? featuredJobSet : FEATURED_JOB_SET
        ) // Fallback value
        .where("active", "==", true)
        .get();
      if (querySnapshot.empty) {
        // There are no featured jobs
        return undefined;
      } else {
        let featuredJobs = querySnapshot.docs;

        // Sort by dateAdded (not supported by Firestore with get)
        if (featuredJobs.length >= 2) {
          // Only sort if necessary
          featuredJobs.sort((a, b) => {
            const dataA = a.data();
            const dataB = b.data();

            // Converts to Date from Firestore timestamp first using toDate()
            return (
              dataB.dateAdded.toDate().getTime() -
              dataA.dateAdded.toDate().getTime()
            );
          });
        }

        // Return the job data
        return featuredJobs.map((doc) => {
          const jobData = doc.data();
          // Convert non-serializable values
          if (jobData.dateAdded !== undefined) {
            jobData.dateAdded = jobData.dateAdded.toMillis();
          }
          if (jobData.expires !== undefined) {
            jobData.expires = jobData.expires.toMillis();
          }
          return { ...jobData, id: doc.id };
        });
      }
    } catch (error) {
      // Log an event
      firebase.analytics().logEvent("get_featured_jobs", {
        errorCode: error.code,
        errorMsg: error.message,
      });

      // Return as if the user isn't admin/publisher of any jobs
      return undefined;
    }
  }
);

// Slice
export const jobsSlice = createSlice({
  name: "jobs",
  initialState: initialState,
  reducers: {
    updateApplicants: (state, action) => {
      // Find the affected job
      const jobIndex = state.allJobs.findIndex(
        (job) => job.id === action.payload.docId
      );

      // Replace the applicants array
      state.allJobs[jobIndex].applicants = action.payload.applicants;
    },
    updateTrackedBy: (state, action) => {
      // Find the affected job
      const jobIndex = state.allJobs.findIndex(
        (job) => job.id === action.payload.docId
      );

      // Replace the trackedBy array
      state.allJobs[jobIndex].trackedBy = action.payload.trackedBy;
    },
    addJob: (state, action) => {
      state.allJobs.push(action.payload);
    },
    removeJob: (state, action) => {
      state.allJobs = state.allJobs.filter((job) => job.id !== action.payload);
    },
    setJobAsActive: (state, action) => {
      // Find the affected job
      const jobIndex = state.allJobs.findIndex(
        (job) => job.id === action.payload
      );

      // Update the active field
      state.allJobs[jobIndex].active = true;
    },
    setJobAsInactive: (state, action) => {
      // Find the affected job
      const jobIndex = state.allJobs.findIndex(
        (job) => job.id === action.payload
      );

      // Update the active field
      state.allJobs[jobIndex].active = false;
    },
    setJobAsArchived: (state, action) => {
      // Find the affected job
      const jobIndex = state.allJobs.findIndex(
        (job) => job.id === action.payload
      );

      // Update the archived field
      state.allJobs[jobIndex].archived = true;
    },
    updateDraft: (state, action) => {
      // Find the affected job
      const jobIndex = state.allJobs.findIndex(
        (job) => job.id === action.payload.jobId
      );

      // Update the draft field and set flag
      state.allJobs[jobIndex].draft[action.payload.field] =
        action.payload.value;
      state.allJobs[jobIndex].hasDraft = true;
    },
    publishDraft: (state, action) => {
      // Find the affected job
      const jobIndex = state.allJobs.findIndex(
        (job) => job.id === action.payload.jobId
      );

      // Merge draft content and set active
      // We don't add date here because it's not serializable
      state.allJobs[jobIndex] = {
        ...state.allJobs[jobIndex],
        ...action.payload.content,
      };
    },
  },
  extraReducers: {
    [getJobs.pending]: (state, action) => {
      state.isFetching = true;
    },
    [getJobs.fulfilled]: (state, action) => {
      state.isFetching = false;
      // Save indication that jobs have been fetched for the mission area
      if (!state.latestJobsFetched.includes(action.payload.missionAreaId)) {
        state.latestJobsFetched.push(action.payload.missionAreaId);
      }

      if (state.allJobs.length === 0) {
        // Haven't fetched jobs, so just set it to the payload
        state.allJobs = action.payload.jobs;
      } else {
        // Add to all jobs, ensuring no duplicates
        if (action.payload.jobs.length !== 0) {
          // Jobs were fetched, and we already have some jobs in the store
          for (const job of action.payload.jobs) {
            if (
              state.allJobs.find((currentJob) => currentJob.id === job.id) ===
              undefined
            ) {
              // Job is not a duplicate, so concatenate
              state.allJobs.push(job);
            }
          }
        }
      }

      if (action.payload.endOfPosts) {
        // There are no jobs (or no additional jobs) in this mission area
        state.allJobsFetched.push(action.payload.missionAreaId);
      }
    },
    [getJob.fulfilled]: (state, action) => {
      state.allJobs.push(action.payload);
    },
    [getMyJobs.pending]: (state, action) => {
      state.myJobsIsLoading = true;
      state.myJobsHasError = false;
      state.myJobsHasFulfilled = false;
    },
    [getMyJobs.fulfilled]: (state, action) => {
      if (action.payload !== undefined && action.payload.length !== 0) {
        // Jobs were fetched, add and ensure no duplicates
        for (const job of action.payload) {
          if (
            state.allJobs.find((currentJob) => currentJob.id === job.id) ===
            undefined
          ) {
            // Job is not a duplicate, so concatenate
            state.allJobs.push(job);
          }
        }
      }
      state.myJobsIsLoading = false;
      state.myJobsHasError = false;
      state.myJobsHasFulfilled = true;
    },
    [getMyJobs.rejected]: (state, action) => {
      state.isLoading = false;
      state.myJobsHasError = true;
      state.hasFulfilled = true;
    },
    [getFeaturedJobs.pending]: (state, action) => {
      state.featuredJobsIsLoading = true;
      state.featuredJobsHasError = false;
      state.featuredJobsHasFulfilled = false;
    },
    [getFeaturedJobs.fulfilled]: (state, action) => {
      if (action.payload !== undefined && action.payload.length !== 0) {
        // Jobs were fetched, add and ensure no duplicates
        for (const job of action.payload) {
          if (
            state.allJobs.find((currentJob) => currentJob.id === job.id) ===
            undefined
          ) {
            // Job is not a duplicate, so concatenate
            state.allJobs.push(job);
          }
        }
      }
      state.featuredJobsIsLoading = false;
      state.featuredJobsHasError = false;
      state.featuredJobsHasFulfilled = true;
    },
    [getFeaturedJobs.rejected]: (state, action) => {
      state.isLoading = false;
      state.featuredJobsHasError = true;
      state.hasFulfilled = true;
    },
  },
});

// Selectors
export const selectIsFetching = (state) => {
  return state.jobs.isFetching;
};
export const selectJobsByMissionArea = (state, missionAreaId) => {
  return state.jobs.allJobs.filter((job) => {
    if (job.missionAreas !== undefined) {
      return job.missionAreas.includes(missionAreaId) && job.active;
    } else {
      return false;
    }
  });
};
export const selectRecentJobs = (state, missionAreaId) => {
  if (state.jobs.latestJobsFetched.includes(missionAreaId)) {
    // Return the latest jobs only if we've fetched with that intent
    return selectJobsByMissionArea(state, missionAreaId).slice(0, 5);
  } else {
    return [];
  }
};
export const selectLatestJobsFetched = (state, missionAreaId) =>
  state.jobs.latestJobsFetched.includes(missionAreaId);

export const selectJob = (state, jobId) => {
  return state.jobs.allJobs.find((job) => job.id === jobId);
};
export const selectAllJobsFetched = (state, missionAreaId) => {
  return state.jobs.allJobsFetched.find((id) => id === missionAreaId)
    ? true
    : false;
};
export const selectMyJobPosts = (state, uid) => {
  return state.jobs.allJobs.filter((job) => {
    if (job.roles === undefined) {
      return false;
    } else if (Object.keys(job.roles).includes(uid)) {
      // User is an admin or publisher for this job
      return true;
    } else {
      return false;
    }
  });
};
export const selectMyJobsHasFulfilled = (state) =>
  state.jobs.myJobsHasFulfilled;
export const selectFeaturedJobPosts = (state, featuredJobSet) => {
  return state.jobs.allJobs.filter(
    (job) =>
      job.featured ===
      (featuredJobSet !== undefined ? featuredJobSet : FEATURED_JOB_SET) // Fallback value
  );
};
export const selectFeaturedJobsHasFulfilled = (state) =>
  state.jobs.featuredJobsHasFulfilled;

// Exports
export const {
  updateApplicants,
  updateTrackedBy,
  addJob,
  removeJob,
  setJobAsActive,
  setJobAsInactive,
  setJobAsArchived,
  updateDraft,
  publishDraft,
} = jobsSlice.actions;
export default jobsSlice.reducer;
